import { createApi } from '@reduxjs/toolkit/query/react';
import { FinishAdjustment, HaveFinish } from 'utils/finishes';
import { appendCardFilterParams } from 'store/services/cards/search/cardsSearchApi';
import type { SearchRequestArgs } from 'screens/Main/Search/searchState';
import { baseQuery } from '../../baseQuery';
import { SortOrder } from 'types/interfaces/sort';
import {
  CardAddSource,
  CollectionListFetchRequest,
  CollectionListFetchResponse,
} from '../types';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { tradeApi } from '../../trade/tradeApi';
import { tradeOffersApi } from '../../tradeOffers/tradeOffersApi';
import { settingsApi } from '../../settings/settingsApi';

export interface HaveListFetchResponse extends CollectionListFetchResponse {
  haveList: CollectionList[];
}

export interface AddCardToHaveListRequest {
  cardId: number;
  quantity?: number;
  condition?: CardCondition;
  /** Authoritative. Send with the derived `foil` (utils/finishes finishFields) through Track D. */
  finish?: HaveFinish;
  foil?: boolean;
  language?: string;
  /**
   * Applied on CREATE only — an existing row's flag is its owner's own setting
   * and is never overwritten (backend HaveListService.addCardToHaveList).
   *
   * MUST be sent explicitly as `false` for an un-catalogued add from the offer
   * builder: the backend defaults to `true` when omitted, which would publish
   * the row to every forTrade-gated surface (search, holder counts, public
   * profile, following feed, match index, suggestions). Omission fails
   * silently and typechecks clean, so it cannot be caught by the compiler.
   */
  forTrade?: boolean;
  source?: CardAddSource; // telemetry: which surface this add came from
}

/**
 * The upserted row. `quantity` is what distinguishes a CREATE from an
 * INCREMENT: the backend upserts on (userId, cardId, foil, condition), so a
 * response of `quantity === requested` means the row was created by this call
 * and may be removed outright on undo, while anything larger means a
 * pre-existing row was incremented and must only ever be decremented.
 */
export interface AddCardToHaveListResponse {
  id: number;
  userId: number;
  cardId: number;
  quantity: number;
  foil: boolean;
  finish?: HaveFinish;
  condition: CardCondition;
  forTrade: boolean;
  displayPrice?: number | null;
  /** Auto-correct report (D-7); null when the stored finish equals the requested one */
  adjustment?: FinishAdjustment | null;
}

export interface UpdateHaveListCardRequest {
  haveListRecordId: number;
  newCardId?: number;
  quantity?: number;
  condition?: CardCondition;
  finish?: HaveFinish;
  foil?: boolean;
  forTrade?: boolean;
  newCardScryfallId?: string;
}

export interface HaveListBulkImportTextRequest {
  text: string;
}

export interface HaveListBulkImportResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    row: number;
    cardName?: string;
    error: string;
  }>;
  importedCards: Array<{
    id: number;
    cardName: string;
    quantity: number;
  }>;
}

export interface AddSetToHaveListRequest {
  setCodes: string[];
  quantity?: number;
  condition?: string;
  finish?: HaveFinish;
  foil?: boolean;
  forTrade?: boolean;
  skipExisting?: boolean;
  skipBasicLands?: boolean;
  rarities?: string[];
}

export interface AddSetToHaveListResponse {
  success: boolean;
  totalCardsProcessed: number;
  cardsAdded: number;
  cardsSkipped: number;
  errors: Array<{ setCode: string; cardName?: string; error: string }>;
  sets: Array<{ setCode: string; setName: string; cardsAdded: number; cardsSkipped: number }>;
  message: string;
}

export const haveListApi = createApi({
  reducerPath: 'haveListApi',
  baseQuery: baseQuery,
  tagTypes: ['HaveList'],
  endpoints: (builder) => ({
    haveListFetch: builder.infiniteQuery<
      HaveListFetchResponse,
      CollectionListFetchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: pageParam.toString(),
          limit: '10'
        });

        // Only add sort if it has a value
        if (queryArg.sort) {
          params.append('sort', queryArg.sort);
        }

        // Only add direction if it has a value (not undefined or empty string)
        // This fixes the issue where empty string parameters were causing backend parsing errors
        if (queryArg.direction) {
          params.append('direction', queryArg.direction);
        }

        return {
          url: `/have-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
      providesTags: ['HaveList'],
    }),
    haveListFetchPaginated: builder.query<
      HaveListFetchResponse,
      { page: number; sort?: string; direction?: SortOrder; search?: string; forTrade?: boolean; filters?: SearchRequestArgs }
    >({
      query: ({ page, sort, direction, search, forTrade, filters }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '100' // 100 cards per page
        });

        // Only add sort if it has a value
        if (sort) {
          params.append('sort', sort);
        }

        // Only add direction if it has a value
        if (direction) {
          params.append('direction', direction);
        }

        // Only add search if it has a value
        if (search) {
          params.append('search', search);
        }

        // Only add forTrade filter if explicitly set
        if (forTrade !== undefined) {
          params.append('forTrade', forTrade.toString());
        }

        // Card-attribute filters: the exact Search wire names, one casing map.
        if (filters) {
          appendCardFilterParams(params, filters);
        }

        return {
          url: `/have-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['HaveList'],
    }),
    addCardToHaveList: builder.mutation<AddCardToHaveListResponse, AddCardToHaveListRequest>({
      query: (body) => ({
        url: '/have-list/add-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
          // RTK tags only invalidate within one api instance, so 'HaveList'
          // above cannot reach the offer builder's candidate cache. Without
          // this the newly added row stays absent from useSendMatchIndex and
          // the card keeps rendering as unowned right after being added.
          dispatch(tradeOffersApi.util.invalidateTags(['OfferCandidates']));
        } catch {}
      },
    }),
    cardToHaveListQuantityUpdate: builder.mutation<
      void,
      { haveListRecordId: number; change: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/have-list/card/quantity-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, change, sort, direction },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for infinite query
        const patchResult = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            { sort, direction },
            (draft) => {
              draft.pages.forEach((page) => {
                page.totalCards = page.totalCards + change;
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, quantity: item.quantity + change }
                    : item
                );
              });
            }
          )
        );

        // Optimistic update for paginated query - find all cached pages
        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, quantity: item.quantity + change }
                        : item
                    );
                    // Update total cards count
                    draft.totalCards = draft.totalCards + change;
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          // Invalidate match index cache so Send page gets fresh auto-match data
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch (e) {
          // Roll back all optimistic updates on failure
          patchResult.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    removeCardFromHaveList: builder.mutation<
      void,
      { haveListRecordId: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/have-list/remove-card',
        method: 'DELETE',
        body: { haveListRecordId: body.haveListRecordId },
        requiresAuth: true,
      }),
      // Invalidate tags to refresh both infinite and paginated queries
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    updateHaveListCard: builder.mutation<AddCardToHaveListResponse, UpdateHaveListCardRequest>({
      query: (body) => ({
        url: '/have-list/card/update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    toggleTradeStatus: builder.mutation<
      void,
      { haveListRecordId: number; forTrade: boolean }
    >({
      query: (body) => ({
        url: '/have-list/card/trade-status',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, forTrade },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for infinite query
        const infinitePatch = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            {},
            (draft) => {
              draft.pages.forEach((page) => {
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, forTrade }
                    : item
                );
              });
            }
          )
        );

        // Optimistic update for all cached paginated queries
        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, forTrade }
                        : item
                    );
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {
          // Roll back all optimistic updates on failure
          infinitePatch.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    updateHaveFinish: builder.mutation<
      AddCardToHaveListResponse,
      { haveListRecordId: number; finish: HaveFinish }
    >({
      // finish is authoritative; the derived foil rides along through Track D
      // (deploy-order safety) and is what a pre-D backend would read.
      query: ({ haveListRecordId, finish }) => ({
        url: '/have-list/card/update',
        method: 'PATCH',
        body: { haveListRecordId, finish, foil: finish !== 'NONFOIL' },
        requiresAuth: true,
      }),
      // No list invalidation: a refetch re-sorts the list (the backend answers a
      // finish change by writing a NEW row, so under "date added" the edited
      // card jumped to the top). The row is patched IN PLACE from the response
      // instead: new id, finish, quantity and price. When the change merged
      // into an existing row of the target finish, the survivor takes the
      // quantity and the edited row is dropped; only a survivor missing from
      // the cache (another page) falls back to a refetch.
      async onQueryStarted(
        { haveListRecordId, finish },
        { dispatch, queryFulfilled, getState }
      ) {
        const foil = finish !== 'NONFOIL';
        const optimistic = (items: any[]) =>
          items.map((item) => (item.id === haveListRecordId ? { ...item, finish, foil } : item));
        const settle = (items: any[], res: AddCardToHaveListResponse): { items: any[]; converged: boolean } => {
          const oldIndex = items.findIndex((item) => item.id === haveListRecordId);
          if (oldIndex === -1) return { items, converged: true };
          const survivorIndex = items.findIndex((item) => item.id === res.id && item.id !== haveListRecordId);
          const fields = {
            finish: res.finish ?? finish,
            foil: res.foil ?? foil,
            quantity: res.quantity,
            displayPrice: res.displayPrice ?? null,
            // The response carries no priceKey; clearing it drops a marker that
            // belonged to the previous finish rather than showing a stale one.
            priceKey: undefined,
          };
          if (survivorIndex !== -1) {
            const next = items.map((item, i) => (i === survivorIndex ? { ...item, ...fields } : item));
            next.splice(oldIndex, 1);
            return { items: next, converged: true };
          }
          if (res.id !== haveListRecordId && items.length === 0) return { items, converged: false };
          return {
            items: items.map((item, i) => (i === oldIndex ? { ...item, ...fields, id: res.id } : item)),
            converged: true,
          };
        };

        const paginatedArgs: any[] = [];
        const state = getState() as any;
        Object.keys(state.haveListApi?.queries || {}).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = state.haveListApi.queries[key];
            if (query?.data) paginatedArgs.push(query.originalArgs);
          }
        });

        const infinitePatch = dispatch(
          haveListApi.util.updateQueryData('haveListFetch', {}, (draft) => {
            draft.pages.forEach((page) => { page.haveList = optimistic(page.haveList); });
          })
        );
        const paginatedPatches = paginatedArgs.map((args) =>
          dispatch(
            haveListApi.util.updateQueryData('haveListFetchPaginated', args, (draft) => {
              draft.haveList = optimistic(draft.haveList);
            })
          )
        );

        try {
          const { data } = await queryFulfilled;
          let converged = true;
          dispatch(
            haveListApi.util.updateQueryData('haveListFetch', {}, (draft) => {
              draft.pages.forEach((page) => {
                const r = settle(page.haveList, data);
                page.haveList = r.items;
                converged = converged && r.converged;
              });
            })
          );
          paginatedArgs.forEach((args) =>
            dispatch(
              haveListApi.util.updateQueryData('haveListFetchPaginated', args, (draft) => {
                const r = settle(draft.haveList, data);
                draft.haveList = r.items;
                converged = converged && r.converged;
              })
            )
          );
          if (!converged) dispatch(haveListApi.util.invalidateTags(['HaveList']));
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {
          infinitePatch.undo();
          paginatedPatches.forEach((patch) => patch.undo());
        }
      },
    }),
    toggleAlertEnabled: builder.mutation<
      void,
      { haveListRecordId: number; alertEnabled: boolean }
    >({
      query: (body) => ({
        url: '/have-list/card/alert-enabled',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, alertEnabled },
        { dispatch, queryFulfilled, getState }
      ) {
        const infinitePatch = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            {},
            (draft) => {
              draft.pages.forEach((page) => {
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, alertEnabled }
                    : item
                );
              });
            }
          )
        );

        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, alertEnabled }
                        : item
                    );
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          dispatch(settingsApi.util.invalidateTags(['HaveListDefaults']));
        } catch {
          infinitePatch.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    deleteAllHaveListItems: builder.mutation<{ message: string; deletedCount: number }, void>({
      query: () => ({
        url: '/have-list/delete-all',
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    haveListQuantitySummary: builder.query<{ quantities: Record<string, number>; quantitiesByCardId: Record<string, number> }, void>({
      query: () => ({
        url: '/have-list/quantity-summary',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['HaveList'],
    }),
    bulkImportHaveList: builder.mutation<HaveListBulkImportResponse, FormData | HaveListBulkImportTextRequest>({
      query: (body) => ({
        url: '/have-list/bulk-import',
        method: 'POST',
        body,
        requiresAuth: true,
        timeout: 300000, // 5 minute timeout for large imports
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    exportHaveList: builder.query<
      { items: any[]; truncated: boolean },
      { search?: string; forTrade?: boolean }
    >({
      query: ({ search, forTrade }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (forTrade !== undefined) params.append('forTrade', forTrade.toString());
        const qs = params.toString();
        return {
          url: `/have-list/export${qs ? `?${qs}` : ''}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
    batchUpdateHaveList: builder.mutation<
      { successCount: number; totalCount: number; failures: { id: number; error: string }[] },
      { ids: number[]; changes: { condition?: CardCondition; finish?: HaveFinish; foil?: boolean; forTrade?: boolean; alertEnabled?: boolean } }
    >({
      query: (body) => ({
        url: '/have-list/batch-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
          if (arg.changes?.alertEnabled !== undefined) {
            dispatch(settingsApi.util.invalidateTags(['HaveListDefaults']));
          }
        } catch {}
      },
    }),
    bulkManageAlerts: builder.mutation<
      { affectedCount: number; mode: 'enable' | 'disable' },
      {
        minPointValue: number;
        mode: 'enable' | 'disable';
        /**
         * When true, the backend also upserts the user's
         * `defaultHaveListAlert` Settings preference to match `mode`
         * (enable → true, disable → false) in the same transaction.
         */
        autoEnableFuture?: boolean;
      }
    >({
      query: (body) => ({
        url: '/have-list/bulk-manage-alerts',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(settingsApi.util.invalidateTags(['HaveListDefaults']));
        } catch {}
      },
    }),
    addSetToHaveList: builder.mutation<AddSetToHaveListResponse, AddSetToHaveListRequest>({
      query: (body) => ({
        url: '/have-list/add-set',
        method: 'POST',
        body,
        requiresAuth: true,
        timeout: 300000,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
  }),
});

export const {
  useHaveListFetchInfiniteQuery,
  useHaveListFetchPaginatedQuery,
  useHaveListQuantitySummaryQuery,
  useAddCardToHaveListMutation,
  useCardToHaveListQuantityUpdateMutation,
  useRemoveCardFromHaveListMutation,
  useUpdateHaveListCardMutation,
  useToggleTradeStatusMutation,
  useUpdateHaveFinishMutation,
  useToggleAlertEnabledMutation,
  useDeleteAllHaveListItemsMutation,
  useBulkImportHaveListMutation,
  useBatchUpdateHaveListMutation,
  useLazyExportHaveListQuery,
  useAddSetToHaveListMutation,
  useBulkManageAlertsMutation,
} = haveListApi;
